// Query and Mutation
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

// Http Methods
import { getRequest as get, postRequest as post } from '../utils/HttpMethods';

import queryString from 'api/utils/queryStringBuilder';

//Interface
import { Provider, isProvider } from 'interfaces';
import type { ProviderPaymentCycle } from '@billing/validation';
import { DateTime } from 'luxon';

//Sentry
import {
  createEntity,
  hasSiteID,
  updateEntity,
  useGetQuery,
  useListQuery,
  useMutationQuery,
} from '../utils/queries';

export interface DebitMetrics {
  lastMonthRequestAmount: number;
  thisMonthRequestAmount: number;
  thisMonthRejectedAmount: number;
  lastMonthRejectedAmount: number;
}

export interface AllTimeOutstandingResult {
  'all-time-start': string | null;
  public: number;
  'third-party': number;
  direct: number;
}

interface ProviderMetricsResponse {
  'payment-cycle'?: ProviderPaymentCycle | null;
  'all-time-outstanding': AllTimeOutstandingResult;
  'ytd-period': {
    'start-date': string | null;
    'end-date': string | null;
  };
  'current-summary-period': {
    start: string;
    end: string;
    public: {
      paid: number;
      submitted: number;
      outstanding: number;
      'last-submission-upload-date': string | null;
    };
    'third-party': {
      paid: number;
      submitted: number;
      outstanding: number;
      'last-submission-upload-date': string | null;
    };
    direct: {
      paid: number;
      submitted: number;
      outstanding: number;
      'last-submission-upload-date': string | null;
    };
  };
  'last-summary-period': {
    start: string | null;
    end: string | null;
    public: {
      paid: number;
      submitted: number;
      outstanding: number;
      'written-off': number;
      'payment-date': string | null;
    };
    'third-party': {
      paid: number;
      submitted: number;
      outstanding: number;
      'written-off': number;
      'payment-date': string | null;
    };
    direct: {
      paid: number;
      submitted: number;
      outstanding: number;
      'written-off': number;
      'payment-date': string | null;
    };
  };
  ytd: {
    public: {
      paid: number;
      submitted: number;
      outstanding: number;
      'written-off': number;
    };
    'third-party': {
      paid: number;
      submitted: number;
      outstanding: number;
      'written-off': number;
    };
    direct: {
      paid: number;
      submitted: number;
      outstanding: number;
      'written-off': number;
    };
  };
}

export type BulkProviderIngestSkippedReason =
  | 'existing-license'
  | 'blank-line'
  | 'duplicate-in-file';

export interface BulkProviderIngestResult {
  success: boolean;
  totalRows: number;
  createdCount: number;
  skippedCount: number;
  failedCount: number;
  created: Array<{
    row: number;
    license: string;
    firstName: string;
    lastName: string;
  }>;
  skipped: Array<{
    row: number;
    license: string;
    reason: BulkProviderIngestSkippedReason;
  }>;
  failed: Array<{
    row: number;
    license?: string;
    reason: string;
  }>;
}

export type BulkProviderIngestJobStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'COMPLETED_WITH_ERRORS'
  | 'FAILED';

export interface BulkProviderIngestJob {
  jobId: string;
  status: BulkProviderIngestJobStatus;
  createdAt?: string;
  updatedAt?: string;
  bucket: string;
  providerKey: string;
  result?: BulkProviderIngestResult;
  error?: string;
}

export type ProviderDashboardDateBasis = 'billed-date' | 'service-date';
export type ProviderDashboardSummaryMode = 'pay-period' | 'month';

export const providerQueryKeys = {
  all: ['providers'],
  list: () => [...providerQueryKeys.all, 'list'],
  listByOrganization: (organizationID: string) => [
    ...providerQueryKeys.all,
    organizationID,
  ],
  listBySite: (siteID: string) => [...providerQueryKeys.all, siteID],
  get: (id: string) => [...providerQueryKeys.all, id],
  providerMetrics: (
    userID: string,
    siteID: string | string[],
    dateBasis: ProviderDashboardDateBasis,
    summaryMode: ProviderDashboardSummaryMode,
    fiscalYearEnd: number
  ) => [
    ...providerQueryKeys.all,
    'providerMetrics',
    userID,
    siteID,
    dateBasis,
    summaryMode,
    fiscalYearEnd,
  ],
};

/**
 * @description Creates a new provider entity.
 * @param {Provider} provider - The provider entity to create.
 * @returns {Promise<Provider>} The created provider entity.
 */
export const createProvider = async (provider: Provider): Promise<Provider> => {
  return createEntity<Provider>({
    entity: provider,
    path: `/resource/provider`,
    functionName: 'createProvider',
    isEntity: isProvider,
  });
};

/**
 * @description Adds a provider to a site.
 * @param {Object} siteProviderData - The data containing provider ID and site IDs.
 * @param {string} siteProviderData.id - The ID of the provider.
 * @param {string} siteProviderData.sites - The IDs of the sites.
 * @returns {Promise<boolean>} True if the provider was added to the site, false otherwise.
 */
export const addProviderToSite = async ({
  providerID,
  siteID,
}: {
  providerID: string;
  siteID: string;
}): Promise<boolean> => {
  const data = await post('QBilling', `/resource/provider/add`, {
    providerID,
    siteID,
  });
  const parsedData = await data;
  if (!parsedData || !Object.keys(parsedData).length) {
    return false;
  } else {
    return true;
  }
};

/**
 * @description Updates a provider entity.
 * @param {Provider} provider - The provider entity to update.
 * @returns {Promise<Provider>} The updated provider entity.
 */
export const updateProvider = async (provider: Provider): Promise<Provider> => {
  return updateEntity<Provider>({
    entity: provider,
    path: `/resource/provider`,
    functionName: 'updateProvider',
    isEntity: isProvider,
  });
};

/**
 * @description Bulk ingests providers from a file.
 * @param {string} bucket - The bucket containing the provider file.
 * @param {string} providerFile - The provider file to ingest.
 * @returns {Promise<boolean>} True if the providers were ingested successfully, false otherwise.
 */
export const bulkIngestProviders = async (
  bucket: string,
  providerFile: File[]
): Promise<BulkProviderIngestJob | BulkProviderIngestResult> => {
  const data = await post<BulkProviderIngestJob | BulkProviderIngestResult>(
    'QBilling',
    `/resource/provider/bulk`,
    {
      bucket,
      providerFile,
    }
  );
  if (!data) {
    throw new Error('Provider bulk ingest returned no data');
  }
  return data;
};

export const getBulkProviderIngestJob = async ({
  bucket,
  jobId,
}: {
  bucket: string;
  jobId: string;
}): Promise<BulkProviderIngestJob> => {
  const data = await get<BulkProviderIngestJob>(
    'QBilling',
    `/resource/provider/bulk${queryString({ bucket, jobId })}`
  );
  if (!data) {
    throw new Error('Provider bulk ingest job was not found');
  }
  return data;
};

/**
 * @description Lists all providers.
 * @returns {Promise<Provider[]>} The list of all providers.
 */
const listProviders = async (): Promise<Provider[]> =>
  await get<Provider[]>('QBilling', `/resource/provider/all`);

/**
 * @description Hook to list all providers.
 * @param {Object} params - The parameters for the hook.
 * @param {boolean} [params.displayNotification=false] - Whether to display a notification.
 * @returns {UseQueryResult<Provider[], Error>} The query result containing the list of providers or an error.
 */
export const useListProviders = ({
  displayNotification = false,
}: {
  displayNotification?: boolean;
}): UseQueryResult<Provider[], Error> => {
  return useListQuery<Provider>({
    queryKey: providerQueryKeys.list(),
    fetchFunction: listProviders,
    functionName: 'listProviders',
    isEntity: isProvider,
    enabled: true,
    displayNotification: displayNotification,
  });
};

/**
 * @description Lists providers by organization ID.
 * @param {string} organizationID - The ID of the organization to list providers for.
 * @returns {Promise<Provider[]>} The list of providers for the specified organization.
 */
const listProvidersByOrganization = async (
  organizationID: string
): Promise<Provider[]> =>
  await get<Provider[]>(
    'QBilling',
    `/resource/provider/organization${queryString({ organizationID })}`
  );

/**
 * @description Hook to list providers by organization ID.
 * @param {Object} params - The parameters for the hook.
 * @param {string} params.organizationID - The ID of the organization to list providers for.
 * @param {boolean} [params.displayNotification=false] - Whether to display a notification.
 * @returns {UseQueryResult<Provider[], Error>} The query result containing the list of providers or an error.
 */
export const useListProvidersByOrganization = ({
  organizationID,
  displayNotification = false,
}: {
  organizationID: string;
  displayNotification?: boolean;
}): UseQueryResult<Provider[], Error> => {
  return useListQuery<Provider>({
    queryKey: providerQueryKeys.listByOrganization(organizationID),
    fetchFunction: listProvidersByOrganization,
    functionName: 'listProvidersByOrganization',
    isEntity: isProvider,
    params: organizationID,
    enabled: !!organizationID,
    displayNotification: displayNotification,
  });
};

/**
 * @description Lists providers by site ID.
 * @param {string} siteID - The ID of the site to list providers for.
 * @returns {Promise<Provider[]>} The list of providers for the specified site.
 */
const listProvidersBySite = async (siteID: string): Promise<Provider[]> =>
  await get<Provider[]>(
    'QBilling',
    `/resource/provider/site${queryString({ siteID })}`
  );

/**
 * @description Hook to list providers by site ID.
 * @param {Object} params - The parameters for the hook.
 * @param {string} params.siteID - The ID of the site to list providers for.
 * @param {boolean} [params.displayNotification=false] - Whether to display a notification.
 * @returns {UseQueryResult<Provider[], Error>} The query result containing the list of providers or an error.
 */
export const useListProvidersBySite = ({
  siteID,
  displayNotification = false,
}: {
  siteID?: string;
  displayNotification?: boolean;
}): UseQueryResult<Provider[], Error> => {
  return useListQuery<Provider>({
    queryKey: providerQueryKeys.listBySite(siteID),
    fetchFunction: listProvidersBySite,
    functionName: 'listProvidersBySite',
    isEntity: isProvider,
    params: siteID,
    enabled: !!siteID,
    displayNotification: displayNotification,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

const getProvider = async (providerID: string): Promise<Provider> =>
  await get<Provider>(
    'QBilling',
    `/resource/provider${queryString({ id: providerID })}`
  );

export const useGetProvider = (
  providerID: string
): UseQueryResult<Provider, Error> => {
  return useGetQuery<Provider>({
    queryKey: providerQueryKeys.get(providerID),
    fetchFunction: getProvider,
    functionName: 'getProvider',
    isEntity: isProvider,
    params: providerID,
    enabled: !!providerID,
  });
};

/**
 * @description Hook to create a new provider entity.
 * @returns {UseMutationResult<Provider, Error>} The mutation result containing the created provider entity or an error.
 */
export const useCreateProvider = (): UseMutationResult<Provider, Error> => {
  return useMutationQuery<Provider>({
    mutationFn: createProvider,
    successMessage: 'Provider Created',
    errorMessage: 'Failed to create provider',
    queryKey: providerQueryKeys.all,
  });
};

/**
 * @description Hook to update a provider entity.
 * @returns {UseMutationResult<Provider, Error>} The mutation result containing the updated provider entity or an error.
 */
export const useUpdateProvider = (): UseMutationResult<Provider, Error> => {
  return useMutationQuery<Provider>({
    mutationFn: updateProvider,
    successMessage: 'Provider Updated',
    errorMessage: 'Failed to update provider',
    queryKey: providerQueryKeys.all,
  });
};

const linkProvider = async ({
  provider,
  userID,
}: {
  provider: Provider;
  userID: string;
}): Promise<boolean> =>
  await post('QBilling', `/resource/provider/link`, { provider, userID });

/**
 * @description Hook to connect user with all applicable provider entities.
 * @returns {UseMutationResult<Boolean>} The success of the linking operation.
 */
export const useLinkProvider = (): UseMutationResult<boolean> => {
  return useMutationQuery<boolean>({
    mutationFn: linkProvider,
    successMessage: 'Provider Successfully Linked To User Account',
    errorMessage: 'Failed to link provider with user account',
    queryKey: providerQueryKeys.all,
  });
};

const listProvidersByUser = async (userID: string): Promise<Provider[]> =>
  await get<Provider[]>(
    'QBilling',
    `/resource/provider/user${queryString({ userID })}`
  );

export const useListProvidersByUser = (
  userID: string
): UseQueryResult<Provider[], Error> => {
  return useListQuery<Provider>({
    queryKey: providerQueryKeys.get(userID),
    fetchFunction: listProvidersByUser,
    functionName: 'listProvidersByUser',
    isEntity: isProvider,
    params: userID,
    enabled: !!userID,
  });
};

const getProviderMetrics = async ({
  userID,
  siteID,
  today,
  fiscalYearEnd,
  dateBasis,
  summaryMode,
}: {
  userID: string;
  siteID: string | string[];
  today: string;
  fiscalYearEnd: number;
  dateBasis: ProviderDashboardDateBasis;
  summaryMode: ProviderDashboardSummaryMode;
}): Promise<ProviderMetricsResponse> => {
  return await get(
    'QBilling',
    `/resource/provider/metrics${queryString({
      userID,
      siteID,
      today,
      fiscalYearEnd,
      dateBasis,
      summaryMode,
    })}`
  );
};

/**
 * @description Hook to fetch the sum of credits by provider with user id within a specified range.
 * @param {Object} params - The parameters for the request.
 * @param {string} params.userID - The ID of the providers user.
 * @returns {UseQueryResult<ProviderMetricsResponse, Error>} The sum of debits by provider.
 */
export const useGetProviderPortalMetrics = ({
  userID,
  siteID,
  today,
  fiscalYearEnd,
  dateBasis,
  summaryMode,
}: {
  userID: string;
  siteID: string | string[];
  today: string;
  fiscalYearEnd: number;
  dateBasis: ProviderDashboardDateBasis;
  summaryMode: ProviderDashboardSummaryMode;
}): UseQueryResult<ProviderMetricsResponse, Error> => {
  return useGetQuery<ProviderMetricsResponse>({
    queryKey: providerQueryKeys.providerMetrics(
      userID,
      siteID,
      dateBasis,
      summaryMode,
      fiscalYearEnd
    ),
    functionName: 'getProviderMetrics',
    fetchFunction: (params: Parameters<typeof getProviderMetrics>[0]) =>
      getProviderMetrics({ ...params, today: DateTime.utc().toISO() }),
    enabled: !!userID && !!today && hasSiteID(siteID),
    params: {
      userID,
      siteID,
      today,
      fiscalYearEnd,
      dateBasis,
      summaryMode,
    },
    isEntity: (data): data is ProviderMetricsResponse =>
      typeof data === 'object',
  });
};

/**
 * @description Searches the providers based on firstName, lastName, or license
 * @param {string} searchValue - The provider data to search on. Can be either firstName, lastName, or license
 * @returns {Promise<Provider[]>} A promise that resolves to an array of possible matches for the search.
 */
const searchProviders = async (searchValue: string): Promise<Provider[]> => {
  return get<Provider[]>(
    'QBilling',
    `/resource/provider/search${queryString({ searchValue })}`
  );
};

/**
 * @description Hook to search providers based on firstName, lastName, or license.
 * @param {Object} params - The parameters for the query.
 * @param {string} params.searchValue - he provider data to search on. Can be either firstName, lastName, or license
 * @param {boolean} [params.displayNotification=false] - Whether to display a notification.
 * @returns {UseQueryResult<Provider, Error>} The result of the query.
 */
export const useSearchProviders = ({
  searchValue,
  displayNotification = false,
}: {
  searchValue: string;
  displayNotification?: boolean;
}): UseQueryResult<Provider[], Error> => {
  return useListQuery<Provider>({
    queryKey: providerQueryKeys.get(searchValue),
    fetchFunction: searchProviders,
    functionName: 'searchProviders',
    params: searchValue,
    isEntity: isProvider,
    enabled: !!searchValue,
    displayNotification,
  });
};

/**
 * @description Fetches provider by their license number
 * @param {string} license - The license number of the provider
 * @returns {Promise<Provider | null>} A promise that resolves to a provider for that license or null if nothing is found
 */
const getProviderByLicense = async ({
  license,
}: {
  license: string;
}): Promise<Provider> => {
  if (!license || license === '00000') return null;
  return get<Provider>(
    'QBilling',
    `/resource/provider/license${queryString({ license })}`
  );
};

/**
 * @description Hook to fetch the provider by their license
 * @param {string} license - The license of the provider.
 * @returns {UseQueryResult<Provider, null>} The provider for that license or null if nothing is found.
 */
export const useGetProviderByLicense = (
  license: string
): UseQueryResult<Provider, Error> => {
  return useGetQuery<Provider>({
    queryKey: providerQueryKeys.get(license),
    functionName: 'getProviderByLicense',
    fetchFunction: getProviderByLicense,
    enabled: !!license,
    params: { license },
    isEntity: isProvider,
  });
};
